import { type ReactNode, useMemo, useState } from "react";
import { DecisionTool, PlanJob_State, type Scenario } from "@/generated/backend/v1/plan";
import type { Family } from "@/generated/core/v1/family";
import type { FinancialPlanData } from "@/generated/core/v1/financial_plan";
import { Struct } from "@/generated/google/protobuf/struct";
import {
  ageOn,
  formatMonth,
  formatMonthShort,
  fromIso,
  yearFraction,
} from "@/plan-logic/lib/dates";
import { money, moneyShort } from "@/plan-logic/lib/format";
import {
  CASH_KINDS,
  householdRetirement,
  INVESTMENT_KINDS,
  netWorthSeries,
  type Plan,
  sumKinds,
  yearlySeries,
} from "@/plan-logic/plan-view";
import { amount, optAmount } from "./fields";
import { heldFor, type InsuranceFormValues } from "./InsuranceForm";
import { type ChartMarker, OutlookChart } from "./OutlookChart";
import {
  type DecisionFacts,
  dearestDebt,
  decisionDate,
  decisionFacts,
  POINT,
} from "@/plan-logic/decisions/verdict";
import { verdictText } from "@/plan-logic/decisions/verdictWords";

/**
 * A scenario's plan against the household's plan today (the stored plan's Abi run), as v38's module
 * output: a verdict, three chips, a "why" paragraph, and what it does to each priority, then the
 * outlook chart. Every number is a plan-view selection over the two plans (verdict.ts); the client
 * works nothing out. Insurance (v44) keeps the verdict, chips, and "why" against today's plan, but
 * its three views compare the scenario's two event plans: on the coverage held now (the comparison
 * plan) and with the coverage tested.
 */
const LIQUID = new Set([...CASH_KINDS, ...INVESTMENT_KINDS]);

function liquidSeries(plan: Plan) {
  return yearlySeries(plan, (s) => sumKinds(s, LIQUID));
}

/** Tools whose answer is mostly about debt: their middle chip is the interest, not retirement. */
const DEBT_TOOLS = new Set([DecisionTool.HOME, DecisionTool.CAR, DecisionTool.FINANCED_PURCHASE]);

const pctOf = (v: number) => Math.round(v * 100);
const signed = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${money(Math.abs(v))}`;

type View = "cash" | "pri" | "ret";

/**
 * What the three views call the two plans they compare: today's plan against the decision's, or,
 * for insurance (v44), the event on the coverage held now against the event on the coverage tested.
 */
type Lines = {
  /** The chart's labels (the readout and the aria label). */
  a: string;
  b: string;
  /** The retirement cards' headings. */
  cardA: string;
  cardB: string;
  /** The first plan in running text: "your plan today" (runs short in …). */
  planA: string;
  /** How the second plan's funding is described: "with this decision". */
  withB: string;
  /** The first plan's funding in a bar's aria label: "today". */
  shortA: string;
  /** Insurance's event lines: both solid, the first in rose. */
  events?: boolean;
  /** One event line (the coverage tested is what's held now). */
  single?: boolean;
};

const PLAN_LINES: Lines = {
  a: "Your plan today",
  b: "With this decision",
  cardA: "Your plan today",
  cardB: "With this decision",
  planA: "your plan today",
  withB: "with this decision",
  shortA: "today",
};

/** Insurance saved before the two event lines: the event against today's plan, as it was shown. */
const OLD_INSURANCE_LINES: Lines = { ...PLAN_LINES, b: "If it happens, with this coverage" };

/** Insurance (v44): the event happens on both lines; only the coverage in force differs. */
const EVENT_LINES: Lines = {
  a: "It happens, on the coverage you hold now",
  b: "It happens, with the coverage tested",
  cardA: "On the coverage you hold now",
  cardB: "With the coverage tested",
  planA: "the plan on the coverage you hold now",
  withB: "with the coverage tested",
  shortA: "on the coverage held now",
  events: true,
};

const VIEWS: [View, string][] = [
  ["cash", "Cash and assets outlook"],
  ["pri", "Priority completion"],
  ["ret", "Retirement impact"],
];

/** The insurance event on the chart: "death at 45" (from the insured person's birth date). */
function eventMarker(family: Family, values: Record<string, unknown> | undefined) {
  if (!values || typeof values.date !== "string") return undefined;
  const date = fromIso(values.date);
  const person = family.persons.find((p) => p.uuid === values.personUuid);
  if (!date) return undefined;
  const age = person?.dob ? ageOn(person.dob, date) : undefined;
  const what = values.event === "death" ? "death" : "diagnosis";
  return {
    x: yearFraction(date),
    label: age !== undefined ? `${what} at ${age}` : `${what}, ${date.year}`,
    tone: "event" as const,
  };
}

/**
 * Cash and assets outlook: the two plans over the years (today's and the decision's, or insurance's
 * two event plans). The retirement marker is today's plan's.
 */
function Outlook({
  family,
  today,
  first,
  second,
  lines,
  insurance,
  values,
}: {
  family: Family;
  today: FinancialPlanData;
  first: FinancialPlanData;
  second: FinancialPlanData;
  lines: Lines;
  insurance: boolean;
  values?: Record<string, unknown>;
}) {
  const [what, setWhat] = useState<"liquid" | "networth">("liquid");
  const pick = what === "liquid" ? liquidSeries : netWorthSeries;
  const markers: ChartMarker[] = [];
  const ret = householdRetirement(today, family);
  if (ret) markers.push({ x: yearFraction(ret.date), label: `Retirement, ${ret.date.year}` });
  const evt = insurance ? eventMarker(family, values) : undefined;
  if (evt) markers.push(evt);
  const labels: [string, string] = [lines.a, lines.b];
  const subject = lines.single
    ? labels[0].toLowerCase()
    : `${labels[0].toLowerCase()} against ${labels[1].toLowerCase()}`;
  return (
    <div data-testid="decision-outlook">
      <div className="pmode" style={{ marginBottom: 10 }}>
        <button
          type="button"
          className={`chip${what === "liquid" ? " on" : ""}`}
          aria-pressed={what === "liquid"}
          onClick={() => setWhat("liquid")}
        >
          Cash and investments
        </button>
        <button
          type="button"
          className={`chip${what === "networth" ? " on" : ""}`}
          aria-pressed={what === "networth"}
          onClick={() => setWhat("networth")}
        >
          Net worth
        </button>
      </div>
      <OutlookChart
        today={pick(first)}
        decision={pick(second)}
        labels={labels}
        solid={insurance}
        single={lines.single}
        markers={markers}
        title={
          what === "liquid"
            ? "Where it leaves your cash and investments"
            : "Where it leaves your net worth"
        }
        ariaLabel={
          what === "liquid"
            ? `Cash and investments over the plan: ${subject}`
            : `Net worth over the plan: ${subject}`
        }
      />
      {lines.single ? (
        <p className="dmnote">
          Hover anywhere on the chart, or use the arrow keys, to read the line at that year. It
          comes from Abi&rsquo;s run of your plan with the event in it and the coverage you hold now
          paying out on the day.
        </p>
      ) : (
        <p className="dmnote">
          Hover anywhere on the chart, or use the arrow keys, to read both lines at that year. Both
          come from Abi&rsquo;s run of each plan
          {lines.events
            ? ", with the event in both and the coverage in force paying out on the day. Before it, only the premium for the extra coverage separates them; after it, the distance between them is what the extra coverage is for."
            : insurance
              ? "; the event happens only on the second, and the coverage pays out on the day."
              : "."}
        </p>
      )}
    </div>
  );
}

/** Retirement impact: the nest egg each plan builds by retirement, and when. */
function RetirementView({ f, lines }: { f: DecisionFacts; lines: Lines }) {
  if (f.reason === "shortfall") {
    return (
      <p className="dmnote" data-testid="decision-retirement">
        Abi&rsquo;s plan stops where the money runs out
        {f.until ? `, in ${formatMonth(f.until)}` : ""}, so retirement isn&rsquo;t compared.
      </p>
    );
  }
  const { before, after, monthsLater } = f.retirement;
  const card = (k: "a" | "b", lab: string, r: typeof before) => (
    <div className={`iretc ${k}`}>
      <div className="eyebrow">{lab}</div>
      <div className="iretv">{r ? moneyShort(r.details.projectedNestEgg) : "Not reached"}</div>
      <p>
        {r
          ? `nest egg at retirement in ${formatMonth(r.date)}, against the ${money(r.details.nestEggRequired)} you need`
          : f.until
            ? `${lines.single ? "it runs short before then" : `${lines.planA} runs short first`}, in ${formatMonth(f.until)}`
            : "no retirement in this plan"}
      </p>
    </div>
  );
  if (lines.single) {
    return (
      <div className="iret" data-testid="decision-retirement">
        {card("a", lines.cardA, before)}
        <p className="dmnote">
          The nest egg the plan builds by the time you retire if it happens, on the coverage you
          hold now, and what it needs to be, from Abi&rsquo;s run.
        </p>
      </div>
    );
  }
  const d = before && after ? after.details.projectedNestEgg - before.details.projectedNestEgg : 0;
  return (
    <div className="iret" data-testid="decision-retirement">
      {card("a", lines.cardA, before)}
      {card("b", lines.cardB, after)}
      <div className="iretc">
        <div className="eyebrow">The difference</div>
        <div className="iretv">
          {before && after
            ? Math.abs(d) < 0.5
              ? "No change"
              : `${d > 0 ? "+" : "−"}${moneyShort(Math.abs(d))}`
            : "—"}
        </div>
        <p>
          {before && after
            ? monthsLater === 0
              ? "and retirement doesn’t move"
              : `and retirement moves ${Math.abs(monthsLater)} ${Math.abs(monthsLater) === 1 ? "month" : "months"} ${monthsLater > 0 ? "later" : "sooner"}`
            : "not compared"}
        </p>
      </div>
      <p className="dmnote">
        The nest egg each plan builds by the time you retire, and what it needs to be, from
        Abi&rsquo;s run of each.
      </p>
    </div>
  );
}

/**
 * v40's result shell: the answer three ways. `f` compares `first` with `second` (today's plan and
 * the decision's, or insurance's two event plans), each cut where the earlier of them runs short.
 */
function Views({
  f,
  family,
  today,
  first,
  second,
  lines,
  insurance,
  values,
}: {
  f: DecisionFacts;
  family: Family;
  today: FinancialPlanData;
  first: FinancialPlanData;
  second: FinancialPlanData;
  lines: Lines;
  insurance: boolean;
  values?: Record<string, unknown>;
}) {
  const [view, setView] = useState<View>("cash");
  return (
    <div className={`insres${lines.events ? " ev" : ""}`} data-testid="decision-views">
      {lines.single && (
        <p className="dmnote" data-testid="decision-one-line">
          The coverage tested is the coverage held now, so the two lines would be the same: this is
          the one line, the event on the coverage you hold now.
        </p>
      )}
      <div className="instabs">
        {VIEWS.map(([k, l]) => (
          <button
            key={k}
            type="button"
            className={view === k ? "on" : ""}
            aria-pressed={view === k}
            onClick={() => setView(k)}
            data-testid={`decision-view-${k}`}
          >
            {l}
          </button>
        ))}
      </div>
      {view === "cash" && (
        <Outlook
          family={family}
          today={today}
          first={first}
          second={second}
          lines={lines}
          insurance={insurance}
          values={values}
        />
      )}
      {view === "pri" &&
        (f.reason === "shortfall" ? (
          <p className="dmnote" data-testid="decision-priorities-none">
            Abi&rsquo;s plan stops where the money runs out
            {f.until ? `, in ${formatMonth(f.until)}` : ""}, so your priorities aren&rsquo;t
            compared.
          </p>
        ) : f.priorities.length || f.notCompared.length ? (
          <PriorityTail f={f} lines={lines} />
        ) : (
          <p className="dmnote">You haven&rsquo;t set any priorities.</p>
        ))}
      {view === "ret" && <RetirementView f={f} lines={lines} />}
    </div>
  );
}

function VerdictLine({ f, tool }: { f: DecisionFacts; tool: DecisionTool }) {
  const icon = { yes: "\u2713", trade: "!", no: "\u2715" }[f.verdict];
  const { head, rest } = verdictText(f, tool);
  const text = (
    <>
      <b>{head}</b> {rest}
    </>
  );
  return (
    <div className={`dmv ${f.verdict}`} data-testid="decision-verdict" data-verdict={f.verdict}>
      <span className="vi" aria-hidden="true">
        {icon}
      </span>
      <span>{text}</span>
    </div>
  );
}

type Chip = { k: string; v: string; s?: string; t?: "warn" | "good"; lit?: boolean };

/** "after bills" or "after bills to Feb 2027", for a window cut short. */
function windowNote(f: DecisionFacts, what: string): string {
  return f.until && f.months < 12 ? `${what} to ${formatMonthShort(f.until)}` : what;
}

/** The first chip: what's left over each month, or (a job) the change in take-home pay. */
function monthlyChip(f: DecisionFacts, tool: DecisionTool): Chip {
  const lo = f.leftOver;
  const th = f.takeHome;
  const dTake = th.after - th.before;
  const k = tool === DecisionTool.JOB ? "Into the account" : "Left over each month";
  if (!f.months) return { k, v: "Not compared", s: "it runs short within a month" };
  return tool === DecisionTool.JOB
    ? {
        k,
        v: Math.abs(dTake) < 0.5 ? "No change" : `${signed(dTake)}/mo`,
        t: dTake <= -0.5 ? "warn" : dTake >= 0.5 ? "good" : undefined,
        s: `${windowNote(f, "take-home")} ${money(th.after)} a month, was ${money(th.before)}`,
      }
    : {
        k,
        v: money(lo.after),
        t: lo.after < lo.before - 0.5 ? "warn" : lo.after > lo.before + 0.5 ? "good" : undefined,
        s: `${windowNote(f, "after bills")}, was ${money(lo.before)}`,
      };
}

/** The chips' markup (v38's `.dmchip`; `lit` is v42's green "Covered"). */
function ChipRow({ chips }: { chips: Chip[] }) {
  return (
    <div className="dmchips" data-testid="decision-chips">
      {chips.map((c) => (
        <div key={c.k} className={`dmchip${c.t ? ` ${c.t}` : ""}${c.lit ? " lit" : ""}`}>
          <div className="k">{c.k}</div>
          <div className="v">{c.v}</div>
          {c.s && <div className="s">{c.s}</div>}
        </div>
      ))}
    </div>
  );
}

/**
 * Insurance's gap panel (v40–v42): the coverage tested, the coverage needed (not sized: that needs
 * the engine, decision-hub.md §4 item 2), and whether the plan holds up if it happens, read from the
 * same verdict as every module ("Covered" on green when nothing gives way).
 */
function InsuranceChips({
  f,
  family,
  values,
}: {
  f: DecisionFacts;
  family: Family;
  values?: Record<string, unknown>;
}) {
  const v = (values ?? {}) as Partial<InsuranceFormValues> & { individualAmount?: string };
  const death = v.event === "death";
  const held = heldFor({
    people: v.people ?? [],
    personUuid: v.personUuid ?? "",
    event: death ? "death" : "critical_illness",
  });
  const tested = optAmount(v.coverage) ?? optAmount(v.individualAmount) ?? held;
  const i = family.persons.findIndex((p) => p.uuid === v.personUuid);
  const who = i < 0 ? "" : family.persons[i].givenName.trim() || (i ? "your partner" : "you");
  const product = death ? "Life Insurance Coverage" : "Critical Illness Coverage";
  const covered = f.verdict === "yes";
  const partly = f.reason === "priority" || f.reason === "retirement";
  const gap: Chip = covered
    ? {
        k: "No gap",
        v: "Covered",
        t: "good",
        lit: true,
        s: "if it happens, your plan reaches the same place: no priority loses funding",
      }
    : {
        k: "The gap",
        v: partly ? "Partly covered" : "Not covered",
        t: "warn",
        s: f.shortfall
          ? `if it happens, your plan runs short in ${formatMonth(f.shortfall.date)}`
          : f.lost
            ? `${f.lost.name} goes unfunded`
            : f.givesWay
              ? `${f.givesWay.name} drops to ${pctOf(f.givesWay.after)}% funded`
              : f.retirement.monthsLater >= 1 && f.retirement.after
                ? `retirement moves to ${formatMonth(f.retirement.after.date)}`
                : undefined,
      };
  return (
    <ChipRow
      chips={[
        {
          k: `${product} tested`,
          v: money(tested),
          s:
            tested > held
              ? `${money(held)} held now, and ${money(tested - held)} more`
              : tested
                ? `what ${who && who !== "you" ? `${who} holds` : `${who || "they"} hold`} now`
                : "none: the income stops with nothing paid out",
        },
        {
          k: "Coverage needed",
          v: "Not sized yet",
          s: "Abi doesn’t size coverage yet. Test a larger amount to see what closes the gap.",
        },
        gap,
      ]}
    />
  );
}

function Chips({ f, tool }: { f: DecisionFacts; tool: DecisionTool }) {
  const chips: Chip[] = [monthlyChip(f, tool)];
  const until = f.until ? formatMonthShort(f.until) : "";
  if (f.reason === "shortfall" && f.shortfall) {
    // The plan stops where it runs short: when, and against today's plan, nothing after it.
    chips.push(
      {
        k: "Runs short",
        v: formatMonthShort(f.shortfall.date),
        t: "warn",
        s: `about ${money(f.shortfall.amount)} short that month`,
      },
      {
        k: "Your plan today",
        v: f.shortfallBefore
          ? `Runs short ${formatMonthShort(f.shortfallBefore.date)}`
          : "Doesn’t run short",
      },
    );
  } else {
    if (DEBT_TOOLS.has(tool)) {
      const d = f.debt.interestAfter - f.debt.interestBefore;
      const free = f.debt.freeAfter
        ? formatMonthShort(f.debt.freeAfter)
        : until
          ? `not by ${until}`
          : "not in the plan";
      chips.push({
        k: until ? `Interest to ${until}` : "Interest over your plan",
        v: Math.abs(d) < 0.5 ? "No change" : signed(d),
        t: d >= 0.5 ? "warn" : d <= -0.5 ? "good" : undefined,
        s: `debt-free ${free}${f.debt.freeBefore ? `, was ${formatMonthShort(f.debt.freeBefore)}` : ""}`,
      });
    } else {
      const { before, after, monthsLater } = f.retirement;
      chips.push(
        until && !after
          ? {
              k: "Retirement",
              v: "Not compared",
              s: `your plan today runs short first, in ${until}`,
            }
          : {
              k: "Retirement",
              v: after ? formatMonthShort(after.date) : "Not reached",
              t: monthsLater >= 1 ? "warn" : monthsLater <= -1 ? "good" : undefined,
              s: after
                ? `${moneyShort(after.details.projectedNestEgg)} nest egg${
                    before ? `, was ${moneyShort(before.details.projectedNestEgg)}` : ""
                  }`
                : before
                  ? `was ${formatMonthShort(before.date)}`
                  : undefined,
            },
      );
    }
    const w = f.givesWay;
    chips.push(
      w
        ? {
            k: "What gives way",
            v: `${pctOf(w.after)}% funded`,
            t: "warn",
            s: `${w.name}, down from ${pctOf(w.before)}%`,
          }
        : {
            k: "What gives way",
            v: "Nothing",
            t: "good",
            s: f.priorities.length
              ? "every priority keeps its funding"
              : f.notCompared.length
                ? `no priority comes due before ${until}`
                : "you haven’t set any priorities",
          },
    );
  }
  return <ChipRow chips={chips} />;
}

/** "In the first year from October 2026," or "In the 4 months from October 2026,". */
function windowLead(f: DecisionFacts): string {
  const when = f.from ? formatMonth(f.from) : "the start of your plan";
  if (f.months >= 12) return `In the first year from ${when},`;
  return `In the ${f.months === 1 ? "month" : `${f.months} months`} from ${when},`;
}

function Why({ f, dearer }: { f: DecisionFacts; dearer?: ReactNode }) {
  const lo = f.leftOver;
  const { before, after, monthsLater } = f.retirement;
  const dPay = f.debt.paymentAfter - f.debt.paymentBefore;
  const dInt = f.debt.interestAfter - f.debt.interestBefore;
  const short = f.reason === "shortfall";
  const until = f.until ? formatMonth(f.until) : "";
  return (
    <p className="dmwhy" data-testid="decision-why">
      {f.months ? (
        <>
          {windowLead(f)} what&rsquo;s left after the bills{" "}
          {Math.abs(lo.after - lo.before) < 0.5 ? (
            <>
              stays at <b>{money(lo.after)}</b> a month.
            </>
          ) : (
            <>
              goes from {money(lo.before)} to <b>{money(lo.after)}</b> a month.
            </>
          )}{" "}
        </>
      ) : (
        `It runs short within a month of ${f.from ? formatMonth(f.from) : "the start of your plan"}. `
      )}
      {short ? (
        <>
          {f.months > 0 && Math.abs(dPay) >= 0.5 && (
            <>
              Debt payments go from {money(f.debt.paymentBefore)} to {money(f.debt.paymentAfter)} a
              month.{" "}
            </>
          )}
          {dearer}
          Abi&rsquo;s plan stops where the money runs out, so nothing after {until} is compared: not
          retirement, not your priorities, and not the interest you&rsquo;d pay.
        </>
      ) : (
        <>
          {(Math.abs(dPay) >= 0.5 || Math.abs(dInt) >= 0.5) && (
            <>
              {money(f.debt.paymentBefore) === money(f.debt.paymentAfter)
                ? `Debt payments stay at ${money(f.debt.paymentAfter)} a month`
                : `Debt payments go from ${money(f.debt.paymentBefore)} to ${money(f.debt.paymentAfter)} a month`}
              , and interest {until ? `up to ${until}` : "over the plan"}{" "}
              {dInt >= 0 ? "rises" : "falls"} by <b>{money(Math.abs(dInt))}</b>.{" "}
            </>
          )}
          {after && (
            <>
              {before && monthsLater !== 0
                ? `Retirement moves from ${formatMonth(before.date)} to ${formatMonth(after.date)}, `
                : `Retirement stays in ${formatMonth(after.date)}, `}
              with a nest egg of <b>{money(after.details.projectedNestEgg)}</b> against the{" "}
              {money(after.details.nestEggRequired)} you need
              {before ? ` (today: ${money(before.details.projectedNestEgg)})` : ""}.{" "}
            </>
          )}
          {dearer}
          {f.givesWay
            ? `${f.givesWay.name} is what gives way, from ${pctOf(f.givesWay.before)}% to ${pctOf(f.givesWay.after)}% funded.`
            : until
              ? `Nothing due before ${until} has to give way for it.`
              : "Nothing in the plan has to give way for it."}
        </>
      )}
    </p>
  );
}

/** "A, B, and C" (with the serial comma). */
function list(names: string[]): string {
  if (names.length < 3) return names.join(" and ");
  return `${names.slice(0, -1).join(", ")}, and ${names.at(-1)}`;
}

function PriorityTail({ f, lines = PLAN_LINES }: { f: DecisionFacts; lines?: Lines }) {
  // A plan that runs short is a "no" on its own; past the shortfall there is nothing to compare.
  if (f.reason === "shortfall") return null;
  if (!f.priorities.length && !f.notCompared.length) return null;
  return (
    <div className="dmpri" data-testid="decision-priorities">
      <p className="ph">What it does to your priorities</p>
      {f.priorities.map((p) => {
        // The change itself, rounded, and only when it counts for the verdict (a point or more):
        // the difference of two rounded shares showed "−1" for a 0.1-point drop under a "Yes".
        const raw = p.after - p.before;
        const d =
          Math.abs(raw) < POINT ? 0 : Math.sign(raw) * Math.max(1, Math.round(Math.abs(raw) * 100));
        return (
          <div
            className={`dmprow${f.givesWay?.key === p.key ? " gives" : ""}`}
            key={p.key}
            data-testid="decision-priority"
          >
            <span className="pn">{p.name}</span>
            <span
              className="pt"
              role="img"
              aria-label={
                lines.single
                  ? `${p.name}: ${pctOf(p.after)}% funded`
                  : `${p.name}: ${pctOf(p.before)}% funded ${lines.shortA}, ${pctOf(p.after)}% ${lines.withB}`
              }
            >
              {!lines.single && (
                <span className="pw" style={{ width: `${(p.before * 100).toFixed(1)}%` }} />
              )}
              <span
                className={`pf${p.after >= 0.995 ? " full" : ""}`}
                style={{ width: `${(p.after * 100).toFixed(1)}%` }}
              />
            </span>
            <span className="pq">
              {pctOf(p.after)}%{!lines.single && d < 0 && <span className="dn"> &minus;{-d}</span>}
              {!lines.single && d > 0 && <span className="up"> +{d}</span>}
            </span>
          </div>
        );
      })}
      {f.priorities.length > 0 && (
        <p className="dmnote">
          {lines.single
            ? "How much of each priority gets funded if it happens, on the coverage you hold now."
            : `How much of each priority gets funded ${lines.withB}; the faint bar is ${lines.planA}.`}{" "}
          Abi weighs each priority by the score you gave it, so lower scores tend to give way first.
          {f.until &&
            ` Funding counts only what comes due before ${lines.single ? "it" : lines.planA} runs short.`}
        </p>
      )}
      {f.notCompared.length > 0 && f.until && (
        <p className="dmnote" data-testid="decision-not-compared">
          {list(f.notCompared)} {f.notCompared.length === 1 ? "comes" : "come"} after{" "}
          {formatMonth(f.until)}, when {lines.single ? "it" : lines.planA} runs short, so{" "}
          {f.notCompared.length === 1 ? "it isn’t" : "they aren’t"} compared.
        </p>
      )}
    </div>
  );
}

export function ResultPanel({
  family,
  baseline,
  plan,
  comparison,
  tool,
  inputs,
}: {
  family: Family;
  baseline: FinancialPlanData;
  plan: FinancialPlanData;
  /**
   * Insurance: the scenario's comparison plan (the event on the coverage held now; `plan` is the
   * event on the coverage tested). Absent when the two are the same, and for older scenarios.
   */
  comparison?: FinancialPlanData;
  tool: DecisionTool;
  /** The tool's saved form values (the scenario's `inputs`), for the decision's date. */
  inputs?: Struct;
}) {
  const values = useMemo(
    () => (inputs ? (Struct.toJson(inputs) as Record<string, unknown>) : undefined),
    [inputs],
  );
  const insurance = tool === DecisionTool.INSURANCE;
  // Insurance since v44's two lines: the views compare the event plans, never today's plan. A
  // scenario saved before then (no `eventLines` in its inputs) shows as it did, against today's.
  const events = insurance && (!!comparison || values?.eventLines === true);
  const lines: Lines = events
    ? { ...EVENT_LINES, single: !comparison }
    : insurance
      ? OLD_INSURANCE_LINES
      : PLAN_LINES;
  const first = events ? (comparison ?? plan) : baseline;
  // The verdict, chips, "why", and "Covered": the scenario against today's plan, for every tool.
  const f = useMemo(
    () => decisionFacts(family, baseline, plan, decisionDate(values)),
    [family, baseline, plan, values],
  );
  // The views: insurance's two event plans, each cut where the earlier of them runs short.
  const viewFacts = useMemo(
    () => (first === baseline ? f : decisionFacts(family, first, plan, decisionDate(values))),
    [f, first, baseline, family, plan, values],
  );
  // A financed purchase's rate against the dearest debt the household already owes on.
  const apr = tool === DecisionTool.FINANCED_PURCHASE ? amount(String(values?.apr ?? "")) : 0;
  const dear = apr > 0 ? dearestDebt(family) : undefined;
  const dearer =
    dear && apr > dear.rate ? (
      <>
        At {+apr.toFixed(2)}%, this costs more than the {+dear.rate.toFixed(2)}% on your {dear.name}
        , which you already owe.{" "}
      </>
    ) : undefined;
  return (
    <div data-testid="decision-results">
      <VerdictLine f={f} tool={tool} />
      {insurance ? (
        <InsuranceChips f={f} family={family} values={values} />
      ) : (
        <Chips f={f} tool={tool} />
      )}
      <Why f={f} dearer={dearer} />
      {events && (
        <p className="dmnote" data-testid="decision-judged">
          Covered or not is judged against your plan today, where nothing happens: covered means
          that with the event and the coverage tested, your plan doesn&rsquo;t run short, no
          priority loses a point of funding, and retirement doesn&rsquo;t move.{" "}
          {lines.single
            ? "The views below show the event on the coverage you hold now."
            : "The views below compare the event on the coverage you hold now with the event on the coverage tested."}
        </p>
      )}
      <Views
        f={viewFacts}
        family={family}
        today={baseline}
        first={first}
        second={plan}
        lines={lines}
        insurance={insurance}
        values={values}
      />
    </div>
  );
}

/** The right-hand card of a tool: nothing yet, running, failed, or the comparison. */
export function ScenarioResults({
  scenario,
  pending,
  error,
  family,
  baseline,
  waiting,
}: {
  scenario?: Scenario;
  pending: boolean;
  error?: string;
  family: Family;
  baseline: FinancialPlanData;
  /** What the answer side says until Analyze is pressed (the tool's `waiting`). */
  waiting?: string;
}) {
  if (error) {
    return (
      <div className="dz-state" role="alert" data-testid="decision-error">
        <b>That didn&rsquo;t work.</b>
        <span>{error}</span>
      </div>
    );
  }
  if (pending) return <Running label="Sending it to Abi" />;
  if (!scenario) return <Waiting what={waiting} />;
  const state = scenario.job?.state;
  if (state === PlanJob_State.FAILED) {
    return (
      <div className="dz-state" role="alert" data-testid="decision-failed">
        <b>Abi couldn&rsquo;t finish this one.</b>
        <span>{scenario.job?.errorMessage || "Check the numbers and try again."}</span>
      </div>
    );
  }
  if (!scenario.plan) {
    return (
      <Running
        label={
          state === PlanJob_State.RUNNING
            ? "Abi is running your plan with this change"
            : "Waiting for Abi"
        }
      />
    );
  }
  return (
    <ResultPanel
      family={family}
      baseline={baseline}
      plan={scenario.plan}
      comparison={scenario.comparisonPlan}
      tool={scenario.tool}
      inputs={scenario.inputs}
    />
  );
}

/**
 * Before Analyze is pressed, or after an input changes (v43): the answer waits behind the button
 * rather than showing a chart nobody asked for.
 */
export function Waiting({ what }: { what?: string }) {
  return (
    <div className="decwait" data-testid="decision-empty">
      <span className="dwi" aria-hidden="true">
        &#9680;
      </span>
      <b>Set what you want to try, then press Analyze.</b>
      <p>
        {what ??
          "Abi re-runs your whole plan with this change and shows the answer three ways, next to the plan you have today."}
      </p>
    </div>
  );
}

function Running({ label }: { label: string }) {
  return (
    <div className="dz-state" role="status" data-testid="decision-running">
      <div className="wp-spin" aria-hidden="true" />
      <b>{label}</b>
      <span>It runs the whole plan day by day, so it can take a minute.</span>
    </div>
  );
}
